/**
 * Tabela de operações enviadas para aprovação — a mesma em "Minhas operações",
 * no Repositório TAX e em "Pendentes de classificação".
 */

import { Eye, FileText, Trash2 } from "lucide-react";
import * as React from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { abrirPdf } from "@/components/aprovacoes/abrirPdf";
import { ETAPAS, SeloAprovacao } from "@/components/aprovacoes/SeloAprovacao";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useRemoverEnvio } from "@/hooks/queries";
import { useAuth } from "@/lib/auth";
import { dataBR } from "@/lib/format";
import type { EnvioAprovacao } from "@/types";

/** Tela de onde a visualização foi aberta — para o botão de voltar. */
export interface OrigemEnvio {
  para: string;
  rotulo: string;
}

export const caminhoEnvio = (id: string) => `/operacoes-enviadas/${id}`;

export function TabelaEnvios({
  envios,
  origem,
  mostrarAutor = false,
  autorRemove = false,
}: {
  envios: EnvioAprovacao[];
  origem: OrigemEnvio;
  mostrarAutor?: boolean;
  /** Só em "Minhas operações": o autor remove o que ainda não foi classificado. */
  autorRemove?: boolean;
}) {
  const navegar = useNavigate();
  const { ehAdmin } = useAuth();
  const remover = useRemoverEnvio();
  const [alvo, setAlvo] = React.useState<EnvioAprovacao | null>(null);
  // O admin remove qualquer envio, pendente ou já classificado, em qualquer lista.
  const podeRemover = autorRemove || ehAdmin;

  const confirmar = () => {
    if (!alvo) return;
    remover.mutate(alvo.id, {
      onSuccess: () => {
        toast.success("Operação removida.");
        setAlvo(null);
      },
      onError: (erro: Error) => toast.error(erro.message),
    });
  };

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="text-left">Nome do fluxo</TableHead>
            <TableHead className="text-left">Categoria</TableHead>
            <TableHead className="text-left">UF</TableHead>
            {mostrarAutor && (
              <TableHead className="text-left">Enviada por</TableHead>
            )}
            <TableHead className="text-left">Envio</TableHead>
            {ETAPAS.map((etapa) => (
              <TableHead key={etapa.tipo}>{etapa.rotulo}</TableHead>
            ))}
            <TableHead />
          </TableRow>
        </TableHeader>
        <TableBody>
          {envios.map((envio) => {
            const classificado = ETAPAS.some((e) => envio.aprovacoes[e.tipo]);
            return (
              <TableRow key={envio.id}>
                <TableCell className="font-bold">{envio.nome}</TableCell>
                <TableCell>{envio.categoria}</TableCell>
                <TableCell className="whitespace-nowrap">{envio.uf || "—"}</TableCell>
                {mostrarAutor && <TableCell>{envio.autor ?? "—"}</TableCell>}
                <TableCell className="whitespace-nowrap">
                  {dataBR(envio.criadoEm)}
                </TableCell>
                {ETAPAS.map((etapa) => (
                  <TableCell key={etapa.tipo} className="text-center">
                    <SeloAprovacao
                      classificacao={envio.aprovacoes[etapa.tipo]}
                    />
                  </TableCell>
                ))}
                <TableCell className="whitespace-nowrap text-right">
                  <Button
                    size="icon"
                    variant="ghost"
                    title="Visualizar o fluxo"
                    aria-label={`Visualizar o fluxo de ${envio.nome}`}
                    onClick={() =>
                      navegar(caminhoEnvio(envio.id), { state: origem })
                    }
                  >
                    <Eye />
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    disabled={!envio.temPdf}
                    title="Ver PDF da operação"
                    aria-label={`Ver PDF de ${envio.nome}`}
                    onClick={() => abrirPdf(envio)}
                  >
                    <FileText />
                  </Button>
                  {podeRemover && (
                    <Button
                      size="icon"
                      variant="ghost"
                      disabled={classificado && !ehAdmin}
                      title={
                        classificado && !ehAdmin
                          ? "Já classificada — não pode mais ser removida"
                          : "Remover"
                      }
                      aria-label={`Remover ${envio.nome}`}
                      onClick={() => setAlvo(envio)}
                    >
                      <Trash2 className="text-destructive" />
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      <Dialog
        open={alvo !== null}
        onOpenChange={(aberto) => !aberto && setAlvo(null)}
      >
        <DialogContent className="w-[min(28rem,94vw)]">
          <DialogHeader>
            <DialogTitle>Remover operação?</DialogTitle>
            <DialogDescription>
              “{alvo?.nome}” será removida definitivamente
              {alvo && ETAPAS.some((e) => alvo.aprovacoes[e.tipo])
                ? ", junto com as classificações já registradas"
                : ""}
              . Essa ação não pode ser desfeita.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setAlvo(null)}
              disabled={remover.isPending}
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={confirmar}
              disabled={remover.isPending}
            >
              <Trash2 />
              Remover
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
